import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import authService from '../services/authService';
import { v4 as uuidv4 } from 'uuid';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext({
    currentUser: null,
    token: null,
    ghostUserId: null,
    isLoading: true,
    login: async () => {},
    register: async () => {},
    loginWithCode: async () => {},
    logout: () => {},
    handleOAuth2Redirect: () => {},
    refreshUser: async () => {},
    isAdmin: false,
    isSystemAdmin: false,
    isPsychologist: false,
});

const saveUserAndToken = (data) => {
    if (data.token) {
        localStorage.setItem('token', data.token);
    }
    const role = (data.roles && data.roles.length > 0) ? data.roles[0] : (data.role || null);
    const userData = {
        id: data.id,
        username: data.username,
        email: data.email,
        role: role,
        imageUrl: data.imageUrl,
        provider: data.provider,
        passwordIsSet: data.passwordIsSet,
        organization: data.organization || null,
    };
    localStorage.setItem('user', JSON.stringify(userData));
    return userData;
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [token, setToken] = useState(null);
    const [ghostUserId, setGhostUserId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const logout = useCallback(() => {
        const key = `LAST_ACTIVE_CHAT_ID_USER_${currentUser?.id}`;
        localStorage.removeItem(key);
        authService.logout();
        setCurrentUser(null);
        setToken(null);
    }, [currentUser]);

    const refreshUser = useCallback(async () => {
        try {
            const tokenFromStorage = authService.getToken();
            if (!tokenFromStorage) {
                logout();
                return;
            }
            const userInfo = await authService.fetchUserInfo();
            if (userInfo) {
                const updatedUser = saveUserAndToken({ ...userInfo, token: tokenFromStorage });
                if(updatedUser) setCurrentUser(updatedUser);
            }
        } catch (error) {
            console.error("Nie udało się odświeżyć danych użytkownika:", error);
            logout();
        }
    }, [logout]);

    useEffect(() => {
        let storedGhostId = localStorage.getItem('ghostUserId');
        if (!storedGhostId) {
            storedGhostId = uuidv4();
            localStorage.setItem('ghostUserId', storedGhostId);
        }
        setGhostUserId(storedGhostId);

        const storedToken = authService.getToken();
        const storedUser = authService.getCurrentUser();

        if (storedToken && storedUser) {
            try {
                const decodedToken = jwtDecode(storedToken);
                const currentTime = Date.now() / 1000;
                if (decodedToken.exp > currentTime) {
                    setToken(storedToken);
                    setCurrentUser(storedUser);
                } else {
                    logout();
                }
            } catch (e) {
                console.error("Błąd dekodowania tokena przy starcie:", e);
                logout();
            }
        }
        setIsLoading(false);
    }, [logout]);

    const login = async (username, password) => {
        const data = await authService.login(username, password);
        const user = saveUserAndToken(data);
        if(user) {
            setToken(data.token);
            setCurrentUser(user);
        }
    };

    const loginWithCode = async (code) => {
        const data = await authService.loginWithCode(code);
        const user = saveUserAndToken(data);
        if(user) {
            setToken(data.token);
            setCurrentUser(user);
        }
    };

    const register = async (username, email, password) => {
        return await authService.register(username, email, password);
    };

    const handleOAuth2Redirect = useCallback(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const oauthToken = urlParams.get('token');
        const errorParam = urlParams.get('error');

        if (oauthToken) {
            localStorage.setItem('token', oauthToken);
            try {
                refreshUser().then(() => {
                    window.history.replaceState({}, document.title, "/app");
                });
            } catch (e) {
                console.error("Błąd po logowaniu OAuth2:", e);
                logout();
                window.history.replaceState({}, document.title, "/login");
                return false;
            }
            return true;
        } else if (errorParam) {
            console.error("Błąd logowania OAuth2:", errorParam);
            window.history.replaceState({}, document.title, "/login");
            return false;
        }
        return null;
    }, [refreshUser, logout]);

    const value = {
        currentUser,
        token,
        ghostUserId,
        isLoading,
        login,
        register,
        loginWithCode,
        logout,
        handleOAuth2Redirect,
        refreshUser,
        isAdmin: currentUser?.role === 'ROLE_ADMIN' || currentUser?.role === 'ROLE_SYSTEM_ADMIN',
        isSystemAdmin: currentUser?.role === 'ROLE_SYSTEM_ADMIN',
        isPsychologist: currentUser?.role === 'ROLE_PSYCHOLOGIST',
    };

    return (
        <AuthContext.Provider value={value}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);